// Copyright ©️ 2026 eVolve MEP, LLC

import { useCallback } from 'react';

import { useEvolveGet } from '@evolvemep/api-ts';
import { Flex, Stack } from '@mantine/core';

import { AttachmentList } from 'components/Mantine/Attachments/AttachmentList';
import { BasePageHeader } from 'components/Mantine/BasePageHeader';
import { DescriptionEditor } from 'components/Mantine/DetailPage/DescriptionEditor';
import { DetailSection } from 'components/Mantine/DetailPage/DetailSection';
import { useEvolveNavigate } from 'components/Mantine/Navigation/useEvolveNavigate';
import { ShipmentActivityFeed } from 'modules/Shop/Shipments/ShipmentDetail/ShipmentActivityFeed';
import { ShipmentWorkflowButtons } from 'modules/Shop/Shipments/ShipmentDetail/ShipmentWorkflowButtons';

import { type ShipmentDetailPageProps, ShipmentDetailWrapper } from './ShipmentDetailWrapper';
import { ShipmentItemsList } from './ShipmentItemsList/ShipmentItemsList';
import { ShipmentSidePane } from './ShipmentSidePane';
import { useUpdateShipment } from './useUpdateShipment';

const ShipmentDetailPageWrapped = ({ shipment, onShipmentUpdated }: ShipmentDetailPageProps) => {
  const navigate = useEvolveNavigate('Shipping Orders');

  const { apiCall: getShipmentApiCall } = useEvolveGet(`shop/shipment/${shipment.shipmentId}`);
  const refreshShipment = useCallback(() => {
    void getShipmentApiCall().then(onShipmentUpdated);
  }, [getShipmentApiCall, onShipmentUpdated]);

  const { updateShipment, canEditShipment } = useUpdateShipment({ shipment, onShipmentUpdated });

  return (
    <Stack flex={1} style={{ overflow: 'hidden' }} gap={0}>
      <BasePageHeader
        title={shipment.identifier}
        parentPage={{ title: 'Shipping Orders', url: '/shipping-orders' }}
        topRightComponent={
          <ShipmentWorkflowButtons
            shipment={shipment}
            onShipmentUpdated={refreshShipment}
            onShipmentDeleted={() => {
              void navigate('/shipping-orders');
            }}
          />
        }
      />

      <Flex gap="md" flex="1 0 0" pr={8} align="flex-start" style={{ overflowY: 'scroll' }}>
        {/* Left column */}
        <Stack flex={1}>
          <DescriptionEditor
            title={shipment.name}
            description={shipment.description ?? ''}
            updateDescription={(description) => updateShipment({ description })}
            editable={canEditShipment('editNameDescription')}
          />

          <ShipmentItemsList shipment={shipment} onItemsUpdated={refreshShipment} />

          <DetailSection title="Attachments" large>
            <AttachmentList shipmentId={shipment.shipmentId} allowUploads="shipment" />
          </DetailSection>

          <DetailSection title="Activity" large>
            <ShipmentActivityFeed shipment={shipment} />
          </DetailSection>
        </Stack>

        {/* Right sidebar */}
        <Stack miw={300} w={300}>
          <ShipmentSidePane shipment={shipment} onShipmentUpdated={onShipmentUpdated} />
        </Stack>
      </Flex>
    </Stack>
  );
};

export const ShipmentDetailPage = () => (
  <ShipmentDetailWrapper>{(context) => <ShipmentDetailPageWrapped {...context} />}</ShipmentDetailWrapper>
);
